import { hasNativePlugin, isNative } from './native.ts'

/** Is the app ON SCREEN? In a browser: the page's own visibility (`document.visibilityState`). In the native app (M19b, ADR-111) ALSO the app's state:
 *  the Android shell keeps its web view "visible" while the app is in the background (so that the page is not frozen and the native sharing goes on
 *  with the screen off), so the page's visibility alone would say "on screen" in a pocket. What only matters on screen stops there all the same: the
 *  live map's polling, the guidance's refresh, the sync when the person comes back, the browser's one-shot positions, the running clock. The NATIVE
 *  sharing (data/livePosition.ts) does not ask this: it runs with the screen off, which is the point.
 *  The app's state comes from `@capacitor/app` (`appStateChange`), loaded only in the native app and only when its shell carries the plugin
 *  (platform/appState.ts): the browser bundle never loads it, and an older shell without it falls back to the page's visibility. */

export const APP_PLUGIN = 'App'

let appActive = true
const listeners = new Set<() => void>()
let watching = false

function setAppActive(active: boolean): void {
  if (active === appActive) return
  appActive = active
  for (const l of [...listeners]) l()
}

/** Starts following the native app, once (nothing in a browser): its state, and Android's BACK button (platform/appState.ts: back in the app's
 *  history, or the app to the background, never closed). Called at start-up (platform/storage.ts, so BACK is the app's from the sign-in page on) and
 *  by the first question below, whichever comes first. */
export function followNativeApp(): void {
  if (watching) return
  watching = true
  try { if (!isNative() || !hasNativePlugin(APP_PLUGIN)) return } catch { return }    // never in the way of the start-up: the page's visibility then
  void import('./appState.ts').then((m) => Promise.all([m.watchAppState(setAppActive), m.watchBackButton()])).catch(() => undefined)
}
const watchApp = followNativeApp

/** On screen now: the page is not hidden and, in the native app, the app is active. */
export function onScreen(): boolean {
  watchApp()
  return appActive && document.visibilityState !== 'hidden'
}

/** Calls `cb` whenever what `onScreen()` answers may have changed (the page's visibility, the native app's state). Returns the unsubscription. */
export function onScreenChange(cb: () => void): () => void {
  watchApp()
  listeners.add(cb)
  document.addEventListener('visibilitychange', cb)
  return () => { listeners.delete(cb); document.removeEventListener('visibilitychange', cb) }
}

/** Calls `cb` each time the app comes back on screen after it was seen off screen (only transitions observed after this call count). */
export function onBackOnScreen(cb: () => void): () => void {
  let away = !onScreen()
  return onScreenChange(() => {
    if (!onScreen()) { away = true; return }
    if (away) { away = false; cb() }
  })
}
